import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  VerticalTabs,
  VerticalTabsList,
  VerticalTabsTrigger,
  VerticalTabsContent,
} from '../components/ui/Tabs';
import { ProfileTab, SecurityTab, SellerTab, NotificationsTab, DangerZoneTab } from '../components/settings';
import Spinner from '../components/ui/Spinner';

/**
 * Settings Page
 *
 * User settings organized in tabs:
 * - Profile (name, photo, location)
 * - Security (email, password)
 * - Seller (Stripe Connect, payouts)
 * - Notifications (preferences)
 */
export default function Settings() {
  const { user, loading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial tab from URL or default to 'profile'
  const currentTab = searchParams.get('tab') || 'profile';

  const handleTabChange = (value) => {
    setSearchParams({ tab: value });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-midnight min-h-screen">
      {/* Header */}
      <div className="bg-midnight-light border-b border-cream/5">
        <div className="page-container py-8">
          <h1 className="font-display font-bold text-2xl md:text-3xl text-cream">
            Settings
          </h1>
          <p className="text-cream/60 mt-1">
            Manage your account and preferences
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="page-container py-8">
        <VerticalTabs value={currentTab} onValueChange={handleTabChange}>
          <VerticalTabsList className="bg-midnight-light/30 rounded-lg p-1.5 pb-1.5 pr-4 md:rounded-xl md:bg-midnight-light/50 md:border md:border-cream/5 md:!p-3">
            <VerticalTabsTrigger
              value="profile"
              icon={<UserIcon />}
              description="Name, photo, location"
            >
              Profile
            </VerticalTabsTrigger>
            <VerticalTabsTrigger
              value="security"
              icon={<LockIcon />}
              description="Email, password"
            >
              Security
            </VerticalTabsTrigger>
            <VerticalTabsTrigger
              value="seller"
              icon={<CreditCardIcon />}
              description="Stripe, payouts"
            >
              Payments
            </VerticalTabsTrigger>
            <VerticalTabsTrigger
              value="notifications"
              icon={<BellIcon />}
              description="Email preferences"
            >
              Notifications
            </VerticalTabsTrigger>
            <VerticalTabsTrigger
              value="danger"
              icon={<TrashIcon />}
              description="Delete account"
              className="text-red-400 hover:text-red-300 data-[state=active]:text-red-400"
            >
              Danger Zone
            </VerticalTabsTrigger>
          </VerticalTabsList>

          <div className="flex-1 bg-midnight-light/30 rounded-xl border border-cream/5 p-6 md:p-8">
            <VerticalTabsContent value="profile">
              <ProfileTab />
            </VerticalTabsContent>

            <VerticalTabsContent value="security">
              <SecurityTab />
            </VerticalTabsContent>

            <VerticalTabsContent value="seller">
              <SellerTab />
            </VerticalTabsContent>

            <VerticalTabsContent value="notifications">
              <NotificationsTab />
            </VerticalTabsContent>

            <VerticalTabsContent value="danger">
              <DangerZoneTab />
            </VerticalTabsContent>
          </div>
        </VerticalTabs>
      </div>
    </div>
  );
}

// Icons
function UserIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function CreditCardIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}
